import { useStripe } from '@stripe/react-stripe-js';
import { useAuth } from '@youversion/auth';
import { IconButton } from '@youversion/design-system-components';
import ChevronLeftStroked from '@youversion/design-system-icons/lib/ChevronLeftStroked';
import ModalFooter from 'components/ui/modal/Footer';
import ModalHeader from 'components/ui/modal/Header';
import PaymentMethodsBank, { BankAccount } from 'components/views/giving/payment-methods-bank';
import { useCreatePaymentMethod } from 'hooks/useCreatePaymentMethod';
import { useTranslation } from 'next-i18next';
import { useCallback, useState } from 'react';
import { createPortal } from 'react-dom';
import { firebaseLogEvent } from 'utils/firebase';

interface Props {
  header: Element | DocumentFragment | null;
  onAddBank: () => void;
  onBack: () => void;
}

export default function PaymentMethodAddBankDialog({ header, onAddBank, onBack }: Props) {
  const { user } = useAuth();
  const stripe = useStripe();
  const { t } = useTranslation('common');
  const [bankInfo, setBankInfo] = useState<BankAccount>({
    accountNumber: '',
    routingNumber: '',
  });
  const { createPaymentMethod, loading } = useCreatePaymentMethod({ onSuccess: onAddBank });
  const buttonDisabled = bankInfo.routingNumber.length !== 9 || bankInfo.accountNumber.length === 0;

  const handleBankChange = useCallback((newState: { [key: string]: string }) => {
    setBankInfo(prevState => ({ ...prevState, ...newState }));
  }, []);

  const handleClick = () => {
    firebaseLogEvent('GivingButtonTap', { label: 'Done', screen: 'giving_add_bank_account' });
    createPaymentMethod(stripe, null, {
      billing_details: {
        email: user?.email,
        name: `${user?.firstName} ${user?.lastName}`,
      },
      type: 'us_bank_account',
      us_bank_account: {
        account_holder_type: 'individual',
        account_number: bankInfo.accountNumber,
        routing_number: bankInfo.routingNumber,
      },
    });
  };

  return (
    <>
      {header
        ? createPortal(
            <ModalHeader
              left={
                <IconButton
                  className='inline-end-2 block-start-2'
                  data-testid='modal-back-button'
                  onClick={() => {
                    firebaseLogEvent('GivingButtonTap', { label: 'Back', screen: 'giving_add_bank_account' });
                    onBack();
                  }}
                >
                  <span className='sr-only'>{t('giving.navigation.back')}</span>
                  <ChevronLeftStroked className='text-text-light dark:text-text-dark rtl:rotate-180' />
                </IconButton>
              }
              title={t('giving.titles.bank_account')}
            />,
            header
          )
        : null}
      <div className='flex flex-col justify-between h-full pli-3 pbs-0.5'>
        <PaymentMethodsBank onChange={handleBankChange} />
      </div>
      <ModalFooter
        buttonText={t('giving.cta.done')}
        disabled={buttonDisabled || loading}
        loading={loading}
        onClick={handleClick}
      />
    </>
  );
}
